import { produce } from 'immer';
import { ReactNode, useCallback } from 'react';
import { getLegendMode } from '@/core';
import { CompactListLegend } from './CompactListLegend';
import { ListLegendItemProps } from './ListLegendItem';
import {
  DEFAULT_LEGEND_DENSITY,
  getAllLegendItemsSelectedState,
  getSingleLegendItemSelectedState,
  isOnlyLegendItemSelected,
  LegendComponentOptions,
  LegendDensity,
  LegendItem,
  SelectedLegendItemState,
  shouldRenderCompactListLegend,
} from './legend-model';
import { StackedListLegend } from './StackedListLegend';
import { TableLegend, TableLegendProps } from './TableLegend';

export interface LegendProps {
  width: number;
  height: number;
  data: LegendItem[];
  options: LegendComponentOptions;

  /**
   * State of selected items in the legend.
   *
   * Selected legend item state is a controlled value that should be managed using a
   * combination of this prop and `onSelectedChange`.
   */
  selectedItems: SelectedLegendItemState;

  /**
   * Callback fired when the selected items in the legend changes.
   */
  onSelectedItemsChange: (newSelected: SelectedLegendItemState) => void;

  /**
   * Callback fired when the mouse is moved over a legend item.
   */
  onItemMouseOver?: ListLegendItemProps['onMouseOver'];

  /**
   * Callback fired when the mouse is moved out of a legend item.
   */
  onItemMouseOut?: ListLegendItemProps['onMouseOut'];

  /**
   * Props specific to legend with `mode` set to `table`.
   */
  tableProps?: Pick<
    TableLegendProps,
    | 'columns'
    | 'disableNameColumn'
    | 'enableIconColumn'
    | 'iconColumnHeaderName'
    | 'enableColumnMenu'
    | 'pinnedColumns'
    | 'sortModel'
    | 'onSortModelChange'
    | 'customTableLegendColumnsStart'
  >;
  /**
   * Disables the compact variation of the `List` legend that displays content inline instead of in a list.
   * Primarily used by `ContentWithLegend` to disable the inline legend when using the "quick filter" feature.
   */
  disableCompactLegend?: boolean;

  /**
   * Disables truncation of the list legend items, allowing them to wrap to the next line.
   * Useful when there is enough space, and users may need to view or copy the full legend item name.
   */
  disableListLegendItemTruncation?: ListLegendItemProps['disableListLegendItemTruncation'];

  /**
   * Determines the density of the legend in Table mode. Primarily impacts the height of rows.
   * @default 'compact'
   */
  density?: LegendDensity;
}

/**
 * Component for rendering a visualization Legend. In the majority of cases, this component
 * should be used indirectly through `ContentWithLegend` to ensure consistent layout and
 * rendering of legends with visualizations.
 */
export function Legend({
  width,
  height,
  options,
  data,
  selectedItems,
  onSelectedItemsChange,
  onItemMouseOver,
  onItemMouseOut,
  tableProps,
  disableCompactLegend,
  disableListLegendItemTruncation,
  density = DEFAULT_LEGEND_DENSITY,
}: LegendProps) {
  /**
   * By clicking on an entire legend item, a user can "focus" on that legend item, or reset to all legend items are selected.
   */
  const onLegendItemClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>, itemId: string) => {
      if (isOnlyLegendItemSelected({ id: itemId }, selectedItems)) {
        // Reset to all legend items are selected.
        onSelectedItemsChange('ALL');
      } else {
        // Focus on this legend item.
        onSelectedItemsChange(getSingleLegendItemSelectedState(data, itemId));
      }
    },
    [data, onSelectedItemsChange, selectedItems]
  );

  /**
   * By clicking on a checkbox, a user can select or unselect a particular legend item.
   */
  const onLegendCheckboxClick = useCallback(
    (itemId: string) => {
      const newSelectedItems = produce(selectedItems, (draft) => {
        if (draft === 'ALL') {
          draft = getAllLegendItemsSelectedState(data);
        }

        const isSelected = !!draft[itemId];

        if (isSelected) {
          // Remove item from the set of selected items.
          draft[itemId] = false;
        } else {
          // Add item to the set of selected items.
          draft[itemId] = true;
        }

        return draft;
      });

      onSelectedItemsChange(newSelectedItems);
    },
    [data, selectedItems, onSelectedItemsChange]
  );

  const mode = getLegendMode(options.mode);

  // The bottom legend is displayed as a list when the number of items is too
  // large and requires virtualization. Otherwise, it is rendered more compactly.
  // We do not need this check for the right-side legend because it is always
  // a virtualized list.
  const isCompactListLegend = shouldRenderCompactListLegend({
    mode,
    position: options.position,
    totalLegendItems: data.length,
    disableCompactLegend,
  });
  const commonLegendProps = {
    width,
    height,
    items: data,
    selectedItems,
    onLegendItemClick,
    onItemMouseOver,
    onItemMouseOut,
  };

  let legendContent: ReactNode;
  if (isCompactListLegend) {
    legendContent = (
      <CompactListLegend
        {...commonLegendProps}
        onLegendCheckboxClick={onLegendCheckboxClick}
        disableListLegendItemTruncation={disableListLegendItemTruncation}
      />
    );
  } else if (mode === 'Table') {
    legendContent = (
      <TableLegend
        {...commonLegendProps}
        onSelectedItemsChange={onSelectedItemsChange}
        {...tableProps}
        density={density}
      />
    );
  } else {
    // Non-compact list legend.
    legendContent = (
      <StackedListLegend
        {...commonLegendProps}
        onLegendCheckboxClick={onLegendCheckboxClick}
        disableListLegendItemTruncation={disableListLegendItemTruncation}
      />
    );
  }

  if (options.position === 'Right') {
    return (
      <div
        style={{
          width: width,
          height: height,
        }}
      >
        {legendContent}
      </div>
    );
  }

  // Position bottom
  return (
    <div
      style={{
        width: width,
        // Switch between using height and maxHeight depending on if it's a "compact" legend because
        // the compact legend depends on a flex-based layout, so it can shrink smaller than the
        // height if it does not need all of the space when the legend items are laid out inline.
        // In non-compact use cases, we use an explicit height because the items are laid out one
        // per row, so we can definitively set an explicit height.
        height: isCompactListLegend ? undefined : height,
        maxHeight: isCompactListLegend ? height : undefined,
      }}
    >
      {legendContent}
    </div>
  );
}
